import { useEffect, useMemo, useState } from "react";
import { AlertCircle, Loader2, Plus, RotateCcw, Trash2 } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Alert,
  AlertDescription,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@sportscanner/ui";
import { sportsUpdateSchema } from "@sportscanner/schemas";
import { apiGet, apiPut, getApiErrorCode } from "@/lib/api";
import {
  useMe,
  useSubscriptionActive,
  type MeClub,
  type MeCourt,
  type MeSport,
} from "@/lib/me";
import { SPORT_KEYS, type SportKey } from "@/lib/sports";
import ConfirmDialog from "@/components/confirm-dialog";
import BookingImpactDialog from "@/components/booking-impact-dialog";

interface SportRow {
  id?: string;
  name: SportKey;
  durationInput: string;
  pendingDelete?: boolean;
}

type PendingState = "added" | "removed" | "changed" | null;

interface Props {
  clubId: string;
}

export default function SportsList({ clubId }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { refresh: refreshMe } = useMe();
  const subscriptionActive = useSubscriptionActive();

  const clubQuery = useQuery({
    queryKey: ["admin", "clubs", clubId],
    queryFn: async () => {
      const data = await apiGet<{ club: MeClub }>(`/api/admin/clubs/${clubId}`);
      return data.club;
    },
  });

  const courts: MeCourt[] = clubQuery.data?.courts ?? [];
  const sportsFromServer: MeSport[] = clubQuery.data?.sports ?? [];

  const [rows, setRows] = useState<SportRow[]>([]);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Impact dialog state: set after the preflight finds at least one
  // duration-change that would overlap existing bookings. We only ever surface
  // the first blocker — fixing it is the only path forward, and stacking
  // multiple block dialogs would be noise.
  const [blockImpact, setBlockImpact] = useState<{
    count: number;
  } | null>(null);
  const [preflighting, setPreflighting] = useState(false);

  useEffect(() => {
    setRows(
      sportsFromServer.map((s) => ({
        id: s.id,
        name: s.name as SportKey,
        durationInput: String(s.sessionDurationMinutes),
      })),
    );
    setErrorCode(null);
    // sportsFromServer reference changes per query; serialize for dep
  }, [JSON.stringify(sportsFromServer)]);

  const courtCountBySportId = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of courts) m.set(c.sportId, (m.get(c.sportId) ?? 0) + 1);
    return m;
  }, [courts]);

  const serverDurationById = useMemo(() => {
    const m = new Map<string, number>();
    for (const s of sportsFromServer) m.set(s.id, s.sessionDurationMinutes);
    return m;
  }, [sportsFromServer]);

  const activeRows = rows.filter((r) => !r.pendingDelete);

  // Block re-adding a sport that's already in the list (including pending-delete);
  // user must undo a pending-delete row instead of adding a duplicate.
  const usedNames = useMemo(() => new Set(rows.map((r) => r.name)), [rows]);
  const availableToAdd: SportKey[] = SPORT_KEYS.filter(
    (k) => !usedNames.has(k),
  );
  const [addPick, setAddPick] = useState<SportKey | "">("");

  const isValid =
    activeRows.length >= 1 &&
    activeRows.every((r) => {
      const m = r.durationInput === "" ? NaN : Number(r.durationInput);
      return Number.isInteger(m) && m >= 15 && m <= 240;
    });

  function rowPendingState(row: SportRow): PendingState {
    if (row.pendingDelete) return "removed";
    if (!row.id) return "added";
    const serverDur = serverDurationById.get(row.id);
    const cur = Number(row.durationInput);
    if (serverDur !== undefined && Number.isInteger(cur) && cur !== serverDur) {
      return "changed";
    }
    return null;
  }

  const isDirty = rows.some((r) => rowPendingState(r) !== null);

  function updateRow(index: number, patch: Partial<SportRow>) {
    setRows((rs) => rs.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }

  function removeRow(index: number) {
    setRows((rs) =>
      rs.flatMap((r, i) => {
        if (i !== index) return [r];
        if (r.id) return [{ ...r, pendingDelete: true }];
        return [];
      }),
    );
  }

  function restoreRow(index: number) {
    setRows((rs) =>
      rs.map((r, i) => (i === index ? { ...r, pendingDelete: false } : r)),
    );
  }

  function addRow() {
    if (!addPick) return;
    setRows((rs) => [...rs, { name: addPick, durationInput: "60" }]);
    setAddPick("");
  }

  const mutation = useMutation({
    mutationFn: async () => {
      const parsed = sportsUpdateSchema.safeParse({
        sports: activeRows.map((r) => ({
          id: r.id,
          name: r.name,
          sessionDurationMinutes: Number(r.durationInput),
        })),
      });
      if (!parsed.success) {
        throw { error: parsed.error.issues[0]?.message ?? "VALIDATION_FAILED" };
      }
      return apiPut<{ ok: true; sports: MeSport[] }>(
        `/api/admin/clubs/${clubId}/sports`,
        parsed.data,
      );
    },
    onSuccess: async () => {
      setErrorCode(null);
      toast.success(t("sportsManage.savedToast"));
      await queryClient.invalidateQueries({
        queryKey: ["admin", "clubs", clubId],
      });
      await refreshMe();
    },
    onError: (err) => {
      setErrorCode(getApiErrorCode(err));
    },
  });

  // Preflight every duration change in parallel. Bail at the first block —
  // the user has to clear it before any of the other changes can save anyway.
  async function handleSaveClick() {
    if (!isValid || !isDirty || mutation.isPending || preflighting) return;
    const durationChanges = activeRows.filter(
      (r) =>
        r.id &&
        rowPendingState(r) === "changed" &&
        Number(r.durationInput) !== serverDurationById.get(r.id),
    );
    if (durationChanges.length === 0) {
      setConfirmOpen(true);
      return;
    }
    setPreflighting(true);
    try {
      const results = await Promise.all(
        durationChanges.map(async (r) => {
          const params = new URLSearchParams({
            kind: "sport-duration",
            sportId: r.id as string,
            sessionDurationMinutes: r.durationInput,
          });
          return apiGet<{ severity: string; count: number }>(
            `/api/admin/clubs/${clubId}/edit-impact?${params.toString()}`,
          );
        }),
      );
      const blocker = results.find((r) => r.severity === "block");
      if (blocker) {
        setBlockImpact({ count: blocker.count });
        return;
      }
      setConfirmOpen(true);
    } catch (err) {
      // Network-level preflight failure shouldn't strand the user — fall back
      // to the generic confirm; the server-side guard still backstops.
      setErrorCode(getApiErrorCode(err));
      setConfirmOpen(true);
    } finally {
      setPreflighting(false);
    }
  }

  function handleConfirm() {
    setConfirmOpen(false);
    mutation.mutate();
  }

  if (clubQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
      </div>
    );
  }

  const saveDisabled =
    !isValid ||
    !isDirty ||
    mutation.isPending ||
    preflighting ||
    !subscriptionActive;

  return (
    <>
      <div className="space-y-4">
        {errorCode && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{t(`errors.api.${errorCode}`)}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-2">
          {rows.map((row, i) => {
            const courtCount = row.id
              ? (courtCountBySportId.get(row.id) ?? 0)
              : 0;
            const deleteBlocked = courtCount > 0;
            const pending = rowPendingState(row);
            const showCourtCount = !pending && deleteBlocked;
            return (
              <div
                key={row.id ?? `new-${row.name}-${i}`}
                className={`flex flex-wrap items-center gap-3 px-4 py-3 rounded-lg border ${
                  pending === "removed"
                    ? "border-red-200 bg-red-50/40"
                    : pending
                      ? "border-amber-200 bg-amber-50/40"
                      : "border-neutral-200 bg-white"
                }`}
              >
                <span
                  className={`text-sm font-medium min-w-[7rem] ${
                    row.pendingDelete
                      ? "text-neutral-400 line-through"
                      : "text-neutral-800"
                  }`}
                >
                  {t(`sports.${row.name}`)}
                </span>
                <div className="flex items-center gap-2">
                  <Label
                    htmlFor={`dur-${i}`}
                    className={`text-xs ${row.pendingDelete ? "text-neutral-400" : "text-neutral-600"}`}
                  >
                    {t("onboarding.sports.durationLabel")}
                  </Label>
                  <Input
                    id={`dur-${i}`}
                    type="number"
                    inputMode="numeric"
                    min={15}
                    max={240}
                    placeholder="60"
                    value={row.durationInput}
                    onChange={(e) =>
                      updateRow(i, { durationInput: e.target.value })
                    }
                    disabled={mutation.isPending || row.pendingDelete}
                    className="rounded-lg border-neutral-200 w-24 h-8"
                  />
                  <span
                    className={`text-xs ${row.pendingDelete ? "text-neutral-400" : "text-neutral-500"}`}
                  >
                    {t("onboarding.sports.minutes")}
                  </span>
                </div>
                <div className="ml-auto flex items-center gap-3">
                  {pending && (
                    <span
                      className={`text-xs ${pending === "removed" ? "text-red-600" : "text-amber-600"}`}
                    >
                      {t(`sportsManage.pending.${pending}`)}
                    </span>
                  )}
                  {showCourtCount && (
                    <span className="text-xs text-neutral-400">
                      {t("sportsManage.deleteBlocked", {
                        count: courtCount,
                      })}
                    </span>
                  )}
                  {row.pendingDelete ? (
                    <button
                      type="button"
                      onClick={() => restoreRow(i)}
                      disabled={mutation.isPending}
                      className="text-xs font-medium text-primary-600 hover:text-primary-700 transition-colors flex items-center gap-1 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                      aria-label={t("sportsManage.pending.undo")}
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                      {t("sportsManage.pending.undo")}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => removeRow(i)}
                      disabled={mutation.isPending || deleteBlocked}
                      className="text-neutral-400 hover:text-red-500 transition-colors p-1 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                      aria-label={t("common.delete")}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {availableToAdd.length > 0 && (
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <Select
              value={addPick}
              onValueChange={(v) => setAddPick(v as SportKey)}
              disabled={mutation.isPending}
            >
              <SelectTrigger className="rounded-lg border-neutral-200 w-full sm:w-56">
                <SelectValue placeholder={t("sportsManage.addSport")} />
              </SelectTrigger>
              <SelectContent>
                {availableToAdd.map((k) => (
                  <SelectItem key={k} value={k}>
                    {t(`sports.${k}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="button"
              variant="outline"
              onClick={addRow}
              disabled={!addPick || mutation.isPending}
              className="rounded-full"
            >
              <Plus className="w-4 h-4 mr-1.5" />
              {t("sportsManage.addSport")}
            </Button>
          </div>
        )}

        <div className="flex justify-end">
          <Button
            type="button"
            onClick={handleSaveClick}
            disabled={saveDisabled}
            className="bg-primary-600 hover:bg-primary-700 text-white"
          >
            {mutation.isPending || preflighting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t("common.saving")}
              </>
            ) : (
              t("sportsManage.save")
            )}
          </Button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t("sportsManage.confirmSave.title")}
        description={t("sportsManage.confirmSave.description")}
        confirmLabel={t("common.save")}
        isPending={mutation.isPending}
        onConfirm={handleConfirm}
      />

      <BookingImpactDialog
        open={!!blockImpact}
        onOpenChange={(o) => {
          if (!o) setBlockImpact(null);
        }}
        severity="block"
        count={blockImpact?.count ?? 0}
        title={t("bookingImpact.sportDuration.blockTitle")}
        description={t("bookingImpact.sportDuration.blockDescription")}
      />
    </>
  );
}
